import { useState, useEffect, useCallback, useRef } from "react";
import { Upload, X, Plus, ChevronUp, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, img, label } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export const getPath = (o, p) => p.split(".").reduce((a, k) => (a == null ? a : a[k]), o);
export const setPath = (o, p, v) => {
  const keys = p.split(".");
  const out = Array.isArray(o) ? [...o] : { ...o };
  let cur = out;
  keys.slice(0, -1).forEach((k) => { cur[k] = { ...(cur[k] || {}) }; cur = cur[k]; });
  cur[keys[keys.length - 1]] = v;
  return out;
};

export const Page = ({ title, sub, actions, children, testid }) => (
  <div className="p-5 sm:p-8 lg:p-10 space-y-8" data-testid={testid}>
    <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[#E5E5E5] pb-6">
      <div><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid="admin-page-title">{title}</h1>{sub && <p className="text-sm text-[#666] mt-1">{sub}</p>}</div>
      <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
    {children}
  </div>
);

export const Pill = ({ s }) => {
  const dark = ["confirmed", "completed", "paid", "converted", "won", "published", "open", "processed", "captured"].includes(s);
  const muted = ["cancelled", "lost", "draft", "closed", "failed", "refunded"].includes(s);
  return <span className={`inline-block px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider border whitespace-nowrap ${dark ? "bg-black text-white border-black" : muted ? "border-[#E5E5E5] text-[#666] line-through decoration-1" : "border-black"}`} data-testid={`pill-${s}`}>{label(s)}</span>;
};

export const Pager = ({ data, page, setPage }) =>
  !data || data.pages <= 1 ? null : (
    <div className="flex items-center gap-3 text-sm" data-testid="admin-pager">
      <button className="btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
      <span>Page {page} of {data.pages} · {data.total} records</span>
      <button className="btn-secondary btn-sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)}>Next</button>
    </div>
  );

export const Table = ({ cols, rows, onRow, empty = "No records found.", testid = "admin-table" }) => (
  <div className="border border-[#E5E5E5] bg-white overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm min-w-[720px]">
      <thead><tr className="border-b border-black text-left">{cols.map((c) => <th key={c.k} className="px-4 py-3 text-[11px] font-mono uppercase tracking-wider text-[#666] font-medium">{c.label}</th>)}</tr></thead>
      <tbody>
        {!rows ? <tr><td colSpan={cols.length} className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr> :
          !rows.length ? <tr><td colSpan={cols.length} className="p-10 text-center text-[#666]" data-testid="table-empty">{empty}</td></tr> :
            rows.map((r, i) => (
              <tr key={r.id || i} onClick={onRow ? () => onRow(r) : undefined} className={`border-b border-[#E5E5E5] ${onRow ? "cursor-pointer hover:bg-[#F5F5F5]" : ""}`} data-testid={`row-${r.reference || r.slug || r.id || i}`}>
                {cols.map((c) => <td key={c.k} className="px-4 py-3 align-top">{c.render ? c.render(r) : r[c.k]}</td>)}
              </tr>
            ))}
      </tbody>
    </table>
  </div>
);

export function useLoad(fn, deps) {
  const [data, setData] = useState(null);
  const load = useCallback(() => fn().then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), deps); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  return [data, load, setData];
}

export async function run(promise, ok) {
  try { const r = await promise; if (ok) toast.success(ok); return r; } catch (e) { toast.error(errMsg(e)); throw e; }
}

export function ImageInput({ value, onChange, testid = "image-input" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const up = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { const r = await api.post("/admin/upload", fd); onChange(r.data.url); toast.success("Image uploaded"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <div className="flex gap-3 items-start">
      {value ? <img src={img(value)} alt="" className="h-16 w-24 object-cover border border-[#E5E5E5]" /> : <div className="h-16 w-24 bg-[#F5F5F5] border border-dashed border-[#E5E5E5]" />}
      <div className="flex-1 space-y-2">
        <input className="field" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload" data-testid={`${testid}-url`} />
        <div className="flex gap-2">
          <button type="button" className="btn-secondary btn-sm" onClick={() => ref.current.click()} disabled={busy} data-testid={`${testid}-upload`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}Upload</button>
          {value && <button type="button" className="btn-secondary btn-sm" onClick={() => onChange("")}>Remove</button>}
        </div>
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => up(e.target.files[0])} />
      </div>
    </div>
  );
}

export function ListInput({ value = [], onChange, placeholder, testid }) {
  const [v, setV] = useState("");
  const add = () => { if (v.trim()) { onChange([...value, v.trim()]); setV(""); } };
  return (
    <div className="space-y-2" data-testid={testid}>
      {value.map((x, i) => (
        <div key={i} className="flex items-center gap-2 border border-[#E5E5E5] px-3 py-1.5 text-sm">
          <span className="flex-1">{x}</span>
          <button type="button" aria-label="Remove" onClick={() => onChange(value.filter((_, j) => j !== i))}><X className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <div className="flex gap-2"><input className="field" value={v} placeholder={placeholder || "Add item and press Enter"} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid={testid && `${testid}-input`} /><button type="button" className="btn-secondary btn-sm" onClick={add}><Plus className="h-3 w-3" /></button></div>
    </div>
  );
}

export function ObjList({ value = [], onChange, fields, addLabel = "Add", testid }) {
  const move = (i, d) => { const a = [...value]; [a[i], a[i + d]] = [a[i + d], a[i]]; onChange(a); };
  return (
    <div className="space-y-3" data-testid={testid}>
      {value.map((item, i) => (
        <div key={i} className="border border-[#E5E5E5] p-3 flex gap-3">
          <div className="flex-1 grid gap-2">
            {fields.map((f) => f.type === "textarea"
              ? <textarea key={f.k} rows={2} className="field" placeholder={f.label} value={item[f.k] || ""} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, [f.k]: e.target.value } : x)))} />
              : f.type === "image" ? <ImageInput key={f.k} value={item[f.k]} onChange={(v) => onChange(value.map((x, j) => (j === i ? { ...x, [f.k]: v } : x)))} />
                : <input key={f.k} className="field" placeholder={f.label} value={item[f.k] || ""} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, [f.k]: e.target.value } : x)))} />)}
          </div>
          <div className="flex flex-col gap-1">
            <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)} className="disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
            <button type="button" aria-label="Move down" disabled={i === value.length - 1} onClick={() => move(i, 1)} className="disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
            <button type="button" aria-label="Remove" onClick={() => onChange(value.filter((_, j) => j !== i))}><X className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
      <button type="button" className="btn-secondary btn-sm" onClick={() => onChange([...value, {}])} data-testid={testid && `${testid}-add`}><Plus className="h-3 w-3" />{addLabel}</button>
    </div>
  );
}

export function Fields({ fields, value, onChange, cols = 2 }) {
  return (
    <div className={`grid gap-4 ${cols === 2 ? "sm:grid-cols-2" : ""}`}>
      {fields.map((f) => {
        const v = getPath(value, f.k);
        const set = (nv) => onChange(setPath(value, f.k, nv));
        const id = `fld-${f.k.replace(/\./g, "-")}`;
        const wide = ["textarea", "image", "list", "objlist"].includes(f.type) || f.wide;
        let input;
        if (f.type === "textarea") input = <textarea id={id} rows={f.rows || 4} className="field" value={v || ""} onChange={(e) => set(e.target.value)} data-testid={id} />;
        else if (f.type === "select") input = <select id={id} className="field" value={v ?? ""} onChange={(e) => set(e.target.value)} data-testid={id}>{f.blank !== false && <option value="">—</option>}{f.opts.map((o) => <option key={o[0] ?? o} value={o[0] ?? o}>{o[1] ?? label(o)}</option>)}</select>;
        else if (f.type === "bool") input = <label className="flex items-center gap-2 text-sm pt-2"><input id={id} type="checkbox" className="h-4 w-4 accent-black" checked={!!v} onChange={(e) => set(e.target.checked)} data-testid={id} />{f.hint || "Enabled"}</label>;
        else if (f.type === "image") input = <ImageInput value={v} onChange={set} testid={id} />;
        else if (f.type === "list") input = <ListInput value={v || []} onChange={set} testid={id} />;
        else if (f.type === "objlist") input = <ObjList value={v || []} onChange={set} fields={f.fields} testid={id} />;
        else if (f.type === "money") input = <input id={id} type="number" min="0" step="1" className="field" value={v == null ? "" : v / 100} onChange={(e) => set(e.target.value === "" ? null : Math.round(Number(e.target.value) * 100))} data-testid={id} />;
        else if (f.type === "number") input = <input id={id} type="number" className="field" value={v ?? ""} onChange={(e) => set(e.target.value === "" ? null : Number(e.target.value))} data-testid={id} />;
        else input = <input id={id} type={f.type || "text"} className="field" value={v || ""} onChange={(e) => set(e.target.value)} placeholder={f.ph} data-testid={id} />;
        return <div key={f.k} className={wide && cols === 2 ? "sm:col-span-2" : ""}><label htmlFor={id} className="field-label">{f.label}</label>{input}{f.help && <p className="text-xs text-[#666] mt-1">{f.help}</p>}</div>;
      })}
    </div>
  );
}

export function FormModal({ open, onClose, title, fields, initial, onSave, testid = "form-modal" }) {
  const [v, setV] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => setV(initial || {}), [initial, open]);
  const save = async (e) => { e.preventDefault(); setBusy(true); try { await onSave(v); onClose(); } catch { /* toast shown */ } finally { setBusy(false); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display text-2xl">{title}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-6">
          <Fields fields={fields} value={v} onChange={setV} />
          <div className="flex justify-end gap-2"><button type="button" className="btn-secondary" onClick={onClose}>Cancel</button><button className="btn-primary" disabled={busy} data-testid={`${testid}-save`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save</button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export const DateRange = ({ frm, to, set }) => (
  <div className="flex items-center gap-2" data-testid="date-range">
    <input type="date" className="field !w-auto !py-1.5" value={frm} onChange={(e) => set(e.target.value, to)} aria-label="From date" data-testid="date-from" />
    <span className="text-xs">to</span>
    <input type="date" className="field !w-auto !py-1.5" value={to} onChange={(e) => set(frm, e.target.value)} aria-label="To date" data-testid="date-to" />
  </div>
);

export const Stat = ({ k, v, testid }) => (
  <div className="card p-5" data-testid={testid}><p className="text-[11px] font-mono uppercase tracking-wider text-[#666]">{k}</p><p className="font-display text-3xl font-bold mt-2">{v ?? "—"}</p></div>
);
